import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getAwaitingInputSurfaceStyles } from "@/shared/utils/awaitingInputStyles";
import type { ReviewEntity, ReviewGap } from "../types";

// ── Props ──────────────────────────────────────────────

interface EntityReviewPanelProps {
  entities: ReviewEntity[];
  gaps: ReviewGap[];
  onUpdateEntity: (id: string, patch: Partial<ReviewEntity>) => void;
  onRemoveEntity: (id: string) => void;
  onRestoreEntity: (id: string) => void;
  onAddEntity: (category: string) => void;
  onReorderEntities: (category: string, fromIndex: number, toIndex: number) => void;
  onResolveGap: (id: string, resolution: string) => void;
  disabled?: boolean;
}

// ── Sortable Entity Item ───────────────────────────────

const confidenceColor: Record<string, "success" | "warning" | "error"> = {
  high: "success",
  medium: "warning",
  low: "error",
};

function SortableEntityItem({
  entity,
  onUpdate,
  onRemove,
  onRestore,
  disabled,
}: {
  entity: ReviewEntity;
  onUpdate: (id: string, patch: Partial<ReviewEntity>) => void;
  onRemove: (id: string) => void;
  onRestore: (id: string) => void;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(entity.label);
  const [editDetails, setEditDetails] = useState(entity.details ?? "");

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entity.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : entity.removed ? 0.4 : 1,
  };

  const handleSave = () => {
    onUpdate(entity.id, {
      label: editLabel.trim() || entity.label,
      details: editDetails.trim() || undefined,
    });
    setEditing(false);
  };

  const handleCancel = () => {
    setEditLabel(entity.label);
    setEditDetails(entity.details ?? "");
    setEditing(false);
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        py: 1,
        px: 1.5,
        borderRadius: 1.5,
        bgcolor: entity.removed ? "action.disabledBackground" : "transparent",
        textDecoration: entity.removed ? "line-through" : "none",
        "&:hover": { bgcolor: entity.removed ? "action.disabledBackground" : "action.hover" },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: disabled ? "default" : "grab",
          display: "flex",
          alignItems: "center",
          pt: 0.5,
          color: "text.disabled",
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <Stack spacing={1}>
            <TextField
              size="small"
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              fullWidth
              autoFocus
              label="Label"
            />
            <TextField
              size="small"
              value={editDetails}
              onChange={(e) => setEditDetails(e.target.value)}
              fullWidth
              label="Details"
            />
            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={handleSave} color="primary">
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleCancel}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, wordBreak: "break-word" }}
            >
              {entity.label}
            </Typography>
            {(entity.details || entity.confidence || entity.source) && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {entity.details && (
                  <Typography variant="caption" color="text.secondary">
                    {entity.details}
                  </Typography>
                )}
                {entity.source && (
                  <Chip label={entity.source} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
                )}
                {entity.confidence && (
                  <Chip
                    label={entity.confidence}
                    size="small"
                    color={confidenceColor[entity.confidence]}
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                )}
              </Stack>
            )}
          </>
        )}
      </Box>

      {!editing && (
        <Stack direction="row" spacing={0}>
          {entity.removed ? (
            <IconButton
              size="small"
              onClick={() => onRestore(entity.id)}
              disabled={disabled}
              title="Restore"
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          ) : (
            <>
              <IconButton
                size="small"
                onClick={() => setEditing(true)}
                disabled={disabled}
                title="Edit"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onRemove(entity.id)}
                disabled={disabled}
                title="Remove"
                color="error"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Stack>
      )}
    </Box>
  );
}

// ── Category Group ─────────────────────────────────────

function CategoryGroup({
  category,
  entities,
  onUpdate,
  onRemove,
  onRestore,
  onAdd,
  onReorder,
  disabled,
}: {
  category: string;
  entities: ReviewEntity[];
  onUpdate: (id: string, patch: Partial<ReviewEntity>) => void;
  onRemove: (id: string) => void;
  onRestore: (id: string) => void;
  onAdd: (category: string) => void;
  onReorder: (category: string, fromIndex: number, toIndex: number) => void;
  disabled?: boolean;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const fromIndex = entities.findIndex((e) => e.id === active.id);
    const toIndex = entities.findIndex((e) => e.id === over.id);
    if (fromIndex !== -1 && toIndex !== -1) {
      onReorder(category, fromIndex, toIndex);
    }
  };

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: 1.5, px: 1.5 }}
      >
        {category}
      </Typography>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={entities.map((e) => e.id)} strategy={verticalListSortingStrategy}>
          {entities.map((entity) => (
            <SortableEntityItem
              key={entity.id}
              entity={entity}
              onUpdate={onUpdate}
              onRemove={onRemove}
              onRestore={onRestore}
              disabled={disabled}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onAdd(category)}
        disabled={disabled}
        sx={{ ml: 1.5, mt: 0.5, textTransform: "none" }}
      >
        Add
      </Button>
    </Box>
  );
}

// ── Gap Item ───────────────────────────────────────────

function GapItem({
  gap,
  onResolve,
  disabled,
}: {
  gap: ReviewGap;
  onResolve: (id: string, resolution: string) => void;
  disabled?: boolean;
}) {
  return (
    <Box
      sx={(theme) => {
        const awaitingInputStyles = getAwaitingInputSurfaceStyles(theme);
        return {
          py: 1.25,
          px: 1.5,
          borderRadius: 1.5,
          backgroundColor: awaitingInputStyles.backgroundColor,
          border: `1px solid ${awaitingInputStyles.borderColor}`,
        };
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <WarningAmberIcon
          fontSize="small"
          sx={(theme) => ({
            mt: 0.25,
            color: getAwaitingInputSurfaceStyles(theme).accentColor,
          })}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {gap.description}
          </Typography>
          <Chip
            label={gap.category}
            size="small"
            variant="outlined"
            sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }}
          />

          {gap.type === "multiple-options" && gap.options?.length ? (
            <RadioGroup
              value={gap.resolution ?? ""}
              onChange={(e) => onResolve(gap.id, e.target.value)}
              sx={{ mt: 1 }}
            >
              {gap.options.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio size="small" disabled={disabled} />}
                  label={<Typography variant="body2">{option}</Typography>}
                />
              ))}
            </RadioGroup>
          ) : (
            <TextField
              size="small"
              placeholder={gap.type === "missing" ? "Enter value..." : "Clarify..."}
              value={gap.resolution ?? ""}
              onChange={(e) => onResolve(gap.id, e.target.value)}
              fullWidth
              disabled={disabled}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
}

// ── Main Panel ─────────────────────────────────────────

export const EntityReviewPanel: React.FC<EntityReviewPanelProps> = ({
  entities,
  gaps,
  onUpdateEntity,
  onRemoveEntity,
  onRestoreEntity,
  onAddEntity,
  onReorderEntities,
  onResolveGap,
  disabled,
}) => {
  if (entities.length === 0 && gaps.length === 0) {
    return (
      <Alert severity="info">
        No structured entities in this outline. Review the outline markdown directly.
      </Alert>
    );
  }

  // Group entities by category, preserving order
  const categories: string[] = [];
  const grouped = new Map<string, ReviewEntity[]>();
  for (const entity of entities) {
    if (!grouped.has(entity.category)) {
      categories.push(entity.category);
      grouped.set(entity.category, []);
    }
    grouped.get(entity.category)!.push(entity);
  }

  return (
    <Stack spacing={2.5}>
      {categories.map((category) => (
        <CategoryGroup
          key={category}
          category={category}
          entities={grouped.get(category)!}
          onUpdate={onUpdateEntity}
          onRemove={onRemoveEntity}
          onRestore={onRestoreEntity}
          onAdd={onAddEntity}
          onReorder={onReorderEntities}
          disabled={disabled}
        />
      ))}

      {gaps.length > 0 && (
        <>
          <Divider />
          <Typography
            variant="overline"
            sx={(theme) => ({
              fontWeight: 700,
              color: getAwaitingInputSurfaceStyles(theme).accentColor,
              letterSpacing: 1.5,
              px: 1.5,
            })}
          >
            Awaiting Clarification ({gaps.length})
          </Typography>
          {gaps.map((gap) => (
            <GapItem
              key={gap.id}
              gap={gap}
              onResolve={onResolveGap}
              disabled={disabled}
            />
          ))}
        </>
      )}
    </Stack>
  );
};
